import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { history, useIntl, useSelector } from 'umi';
import { Layout } from 'tea-component';
import Tabs from '@intelligence/components/NewIconTabs';
import MobileGames from './MobileGames';
import PCConsoleGames from '@intelligence/v3/pages/MarketProfile/PCConsoleGames';
import PCAndConsoleGames from '@intelligence/v3/pages/MarketProfile/PCAndConsoleGames';
import PcGames from './PcGames';
import ConsoleGames from './ConsoleGames';
import AnnualReport from './AnnualReport';
import useMarketFilterApi from '@intelligence/hooks/useMarketSelector';
import { buriedPointPage } from '@home/utils/buriedPoint';
import { dbiPageLogMes } from '@intelligence/utils/buriedPointCfg';
import { namespace as GUIDE_NAMESPACES, GUIDE_TYPE_INTELLIGENCE_GENRE_MARKET_PROFILE } from '@home/models/guide';
import { GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES } from '@intelligence/components/UserGuide/GenreMarketProfile';
import {
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
  PC_ENTITY_TYPE,
  CONSOLE_ENTITY_TYPE,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
// import { MAIN_GENRE_OPTIONS } from './constants';

import '@intelligence/assets/scss/MarketProfile/index.scss';
import './index.scss';

const { Content } = Layout;

const DEFAULT_ACTIVE_ID = 'MobileGames';

/**
 * 页面打点key映射
 * 键：页面tab的id
 * 值：页面打点配置的key
 */
const PAGE_LOG_KEY_MAPPING: IRecord<string> = {
  MobileGames: 'marketProfileMobile',
  PCConsoleGames: 'marketProfileCombine',
  PCGames: 'marketProfilePC',
  ConsoleGames: 'marketProfileConsole',
  AnnualReport: 'marketProfileAnnual',
};

const MarketProfile: React.FC<any> = (props) => {
  const { formatMessage } = useIntl();

  const { guideType } = useSelector<any, any>((rootState) => rootState?.[GUIDE_NAMESPACES] || {});
  const isGenreMarketProfileUserGuide = guideType === GUIDE_TYPE_INTELLIGENCE_GENRE_MARKET_PROFILE;

  // 获取全局国家地区数据
  const { regionOptions } = useMarketFilterApi({ filterType: 'rpd' });

  const [activeId, setActiveId] = useState(props.match?.params?.tab || DEFAULT_ACTIVE_ID);
  const [selectDate, setSelectDate] = useState<string[]>([]);
  const [selectMainGenre, setSelectMainGenre] = useState<any[]>([]);
  const [selectSubGenres, setSelectSubGenres] = useState<any[]>([]);
  // 增加纯国家列表以用来简称匹配全称用
  const [regionsCountries, setRegionsCountries] = useState<any[]>([]);
  const [regionValue, setRegion] = useState({
    region_type: 'market',
    regions: ['global'],
    realMarkets: [],
    markets: ['global'],
  });

  const onActive = (tab: { id: string }) => {
    const tabId = tab?.id || DEFAULT_ACTIVE_ID;
    setActiveId(tabId);
    history.replace(`/intelligence/marketProfile/${tabId}`);
  };

  // 时间筛选横向保存
  const changeSelectDate = (data: string[]) => {
    setSelectDate(data);
  };

  // 品类筛选横向保存
  const changesetSelectGenre = (payload: any) => {
    const { mainGenres, subGenres } = payload || {};
    setSelectMainGenre(mainGenres);
    setSelectSubGenres(subGenres);
  };

  const changeRegionSelect = (data: any) => {
    setRegion(data);
  };

  // 调用打点
  const postLog = (activePageName: string) => {
    const pageLogKey = PAGE_LOG_KEY_MAPPING[activePageName] || PAGE_LOG_KEY_MAPPING.MobileGames;
    buriedPointPage(dbiPageLogMes[pageLogKey]);
  };

  const tabs = [
    {
      id: 'MobileGames',
      label: formatMessage({ id: 'dbi_mobile_games' }),
      className: GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT2,
      component: (
        <MobileGames
          entityType="mobile"
          regionsCountries={regionOptions}
          selectDate={selectDate}
          changeSelectDate={changeSelectDate}
          selectMainGenre={selectMainGenre}
          selectSubGenres={selectSubGenres}
          changesetSelectGenre={changesetSelectGenre}
        // mainGenreOptions={MAIN_GENRE_OPTIONS}
        />
      ),
    },
    {
      id: 'PCConsoleGames',
      label: formatMessage({ id: 'dbi_pcconsole_games' }),
      component: <PCConsoleGames entityType={PC_CONSOLE_COMBINE_ENTITY_TYPE} />,
    },
    {
      id: 'PCGames',
      label: formatMessage({ id: 'dbi_pc_games' }),
      className: GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT3,
      component: <PCAndConsoleGames entityType={PC_ENTITY_TYPE} />,
    },
    {
      id: 'ConsoleGames',
      label: formatMessage({ id: 'dbi_console_games' }),
      className: GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT4,
      component: <PCAndConsoleGames entityType={CONSOLE_ENTITY_TYPE} />,
    },
    {
      id: 'AnnualReport',
      label: formatMessage({ id: 'dbi_market_annual_report' }),
      component: <AnnualReport regionsCountries={regionsCountries} />,
    },
  ];

  useEffect(() => {
    // 手动添加Overall-global
    const regions = regionOptions ? [...regionOptions] : ([] as any);
    setRegionsCountries(regions);
  }, [regionOptions]);

  useEffect(() => {
    if (!activeId) return;
    // 当tab id设置完成后再打点
    postLog(activeId);
  }, [activeId]);

  return (
    <Content className="dbi-marketprofile">
      <Content.Header
        // 这里在Genre Profile、Market Profile这个新手引导中 这个页面的tab切换栏需要吸顶效果 所以加了user-guide-sticky这个className
        className={classnames('dbi-header contentHead-normal', {
          'user-guide-sticky': isGenreMarketProfileUserGuide,
        })}
        title={formatMessage({ id: 'dbi_market_profile' })}
      >
        <div className="header-tab">
          <Tabs
            ceiling
            newMarkIdsMappingKey="MarketProfile"
            animated={false}
            tabs={tabs}
            activeId={activeId}
            onActive={onActive}
          />
        </div>
      </Content.Header>
      <Content.Body full>
        {['PCConsoleGames', 'PCGames', 'ConsoleGames'].includes(activeId) && <MarqueeBanner />}
        {tabs.map((tab) => {
          const tabId = tab?.id || '';
          return <div key={tabId}>{tabId === activeId && tab?.component}</div>;
        })}
      </Content.Body>
    </Content>
  );
};

export default MarketProfile;
